// src/pages/UsersPage.js
// Lists Entra ID + Custom Users, enriched with ownership/assignment across
// SaaS Applications, Local Applications, and Shared Credentials. Systems has
// been removed — this used to only look at Systems for the "Owns/Assigned"
// badges; now it aggregates across all three remaining types.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { saasApplicationsApi, localApplicationsApi, sharedCredentialsApi } from "../utils/api";

const TYPES = [
  { label: "SaaS Applications",  api: saasApplicationsApi  },
  { label: "Local Applications", api: localApplicationsApi },
  { label: "Shared Credentials", api: sharedCredentialsApi },
];

export default function UsersPage() {
  const [people,  setPeople]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [peopleLoaded, setPeopleLoaded] = useState(false); // true once fetched, either via search or "Load all"
  const [search,  setSearch]  = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Load Entra users and custom users
      const [entraRes, customRes] = await Promise.all([
        fetch("/api/graph/users?q=*&limit=999").catch(() => null),
        fetch("/api/custom-users").catch(() => null),
      ]);

      const entraUsers  = entraRes?.ok  ? await entraRes.json().catch(() => [])  : [];
      const customUsers = customRes?.ok ? await customRes.json().catch(() => []) : [];

      // 2. Build map from Entra users
      const map = new Map();

      if (Array.isArray(entraUsers)) {
        for (const u of entraUsers) {
          if (!u.upn) continue;
          map.set(u.upn.toLowerCase(), {
            upn:         u.upn,
            displayName: u.displayName || u.upn,
            email:       u.email || u.upn,
            jobTitle:    u.jobTitle   || "",
            department:  u.department || "",
            source:      "Entra ID",
            ownerOf:     [],
            userOf:      [],
          });
        }
      }

      // 3. Merge custom users
      if (Array.isArray(customUsers)) {
        for (const cu of customUsers) {
          const key = (cu.upn || cu.email || "").toLowerCase();
          if (!key) continue;
          if (!map.has(key)) {
            map.set(key, {
              upn:         cu.upn || cu.email,
              displayName: cu.displayName,
              email:       cu.email,
              jobTitle:    cu.jobTitle || "",
              department:  "External",
              source:      "Custom User",
              ownerOf:     [],
              userOf:      [],
              isCustom:    true,
            });
          }
        }
      }

      // 4. Ownership + assignment across all three resource types
      for (const type of TYPES) {
        let items = [];
        try { items = await type.api.list(); } catch { continue; }

        // Ownership: accessOwners array on each item
        for (const item of items) {
          for (const owner of (item.accessOwners || [])) {
            if (!owner.upn) continue;
            const key = owner.upn.toLowerCase();
            if (map.has(key)) {
              map.get(key).ownerOf.push(item.name);
            } else {
              map.set(key, {
                upn: owner.upn, displayName: owner.displayName || owner.upn,
                email: owner.email || owner.upn, jobTitle: "", department: "",
                source: "Entra ID", ownerOf: [item.name], userOf: [],
              });
            }
          }
        }

        // Assignment: fetch each item's user list (capped to avoid timeouts at scale)
        const itemsToCheck = items.slice(0, 100);
        await Promise.allSettled(itemsToCheck.map(async item => {
          try {
            const data = await type.api.get(item.id);
            for (const u of (data.users || []).filter(u => u.active && u.upn)) {
              const key = u.upn.toLowerCase();
              if (map.has(key)) {
                map.get(key).userOf.push(item.name);
              }
            }
          } catch {}
        }));
      }

      setPeople(Array.from(map.values()).sort((a, b) => a.displayName.localeCompare(b.displayName)));
    } finally {
      setLoading(false);
    }
  }, []);

  // Once people are loaded (via search or "Load all"), everything after that
  // is just client-side filtering — no need to refetch on every keystroke.
  useEffect(() => {
    if (peopleLoaded) return;
    const q = search.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => { setPeopleLoaded(true); load(); }, 350);
    return () => clearTimeout(t);
  }, [search, load, peopleLoaded]);

  const handleLoadAll = () => {
    setPeopleLoaded(true);
    load();
  };

  const filtered = people.filter(p => {
    if (sourceFilter === "entra"  && p.source !== "Entra ID")    return false;
    if (sourceFilter === "custom" && p.source !== "Custom User")  return false;
    const q = search.toLowerCase();
    return !q ||
      p.displayName.toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.department || "").toLowerCase().includes(q) ||
      (p.jobTitle || "").toLowerCase().includes(q);
  });

  const entraCount  = people.filter(p => p.source === "Entra ID").length;
  const customCount = people.filter(p => p.source === "Custom User").length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div className="page-subtitle">
            {!peopleLoaded
              ? "Search for a person, or load everyone, to get started"
              : loading ? "Loading…" : `${people.length} loaded, ${filtered.length} shown — ${entraCount} from Entra ID, ${customCount} custom`}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <input type="text" placeholder="Search by name, email or department…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320 }} autoFocus />
        <div style={{ display: "flex", gap: 4 }}>
          {[["all","All"],["entra","Entra ID"],["custom","Custom"]].map(([val, label]) => (
            <button key={val}
              className={sourceFilter === val ? "btn-primary" : "btn-secondary"}
              style={{ fontSize: 12, padding: "5px 12px" }}
              disabled={!peopleLoaded}
              onClick={() => setSourceFilter(val)}>
              {label}
            </button>
          ))}
        </div>
        {!peopleLoaded && (
          <button className="btn-secondary" style={{ fontSize: 12, padding: "5px 12px" }}
            onClick={handleLoadAll} disabled={loading}>
            {loading ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Loading…</> : "Load all users"}
          </button>
        )}
      </div>

      {!peopleLoaded ? (
        <div className="card empty-state">
          <h3>Search for someone, or load everyone</h3>
          <p>Type at least 2 characters of a name, email, job title, or department — or click <strong>Load all users</strong> above to browse the full list and filter by Entra ID / Custom.</p>
        </div>
      ) : loading ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <span className="spinner" />
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--gray-500)" }}>Loading…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card empty-state">
          <h3>No users match your search</h3>
          <p>Try a different search term{sourceFilter !== "all" ? " or filter" : ""}.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50)" }}>
                {["Name", "Job Title", "Department", "Type", "Access"].map(h => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(person => (
                <tr key={person.upn}
                  onClick={() => navigate(`/users/${encodeURIComponent(person.upn)}`)}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--gray-50)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "11px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%",
                        background: person.isCustom ? "var(--gray-400)" : "var(--purple)",
                        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                        {person.displayName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{person.displayName}</div>
                        <div style={{ fontSize: 11, color: "var(--gray-500)" }}>{person.upn}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "11px 16px", fontSize: 13, color: "var(--gray-700)" }}>{person.jobTitle || "—"}</td>
                  <td style={{ padding: "11px 16px", fontSize: 13, color: "var(--gray-700)" }}>{person.department || "—"}</td>
                  <td style={{ padding: "11px 16px" }}>
                    <span className={`badge ${person.isCustom ? "badge-neutral" : "badge-info"}`} style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                      {person.isCustom ? "Custom" : "Entra"}
                    </span>
                  </td>
                  <td style={{ padding: "11px 16px" }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {person.ownerOf.length > 0 && (
                        <span className="badge badge-info" style={{ fontSize: 11 }}>Owns {person.ownerOf.length}</span>
                      )}
                      {person.userOf.length > 0 && (
                        <span className="badge badge-neutral" style={{ fontSize: 11 }}>Assigned {person.userOf.length}</span>
                      )}
                      {person.ownerOf.length === 0 && person.userOf.length === 0 && (
                        <span style={{ fontSize: 12, color: "var(--gray-400)" }}>None</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
